import React from 'react';

import styled, { css } from 'styled-components';

import { redScrollBar } from 'components/Scrollbar/scrollbar.styles';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import { AwardsBlockI } from 'interfaces/content-stack/modular-blocks/awardsBlock';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const AwardsCopy = styled(InnerHtml)`
  p {
    font-family: ${(props) => props.theme.fonts.default};
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0;
  }

  sup {
    top: -0.5em;
  }
`;

const AwardsBodyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: start;

    > * {
      width: fit-content;
    }
  }
`;

const AwardImagesContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  gap: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    ${redScrollBar()}
    justify-content: flex-start;
    padding-bottom: 3rem;
    padding-left: 3rem;
    padding-right: 3rem;
    overflow-x: scroll;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    justify-content: unset;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    gap: 1rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const StyledImage = styled.img<{ $isLargeImage: boolean }>(
  ({ $isLargeImage }) => css`
    max-width: ${$isLargeImage ? '20%' : '16%'};
    display: inline-block;
    object-fit: contain;
    flex: 1;
    width: 12.8rem;

    @media (max-width: ${(props) => props.theme.breakpoints.xxl}) {
      width: 11.2rem;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
      width: 15vw;
      max-width: none;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      width: 17vw;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      width: 8rem;
    }
  `
);

const AwardImagesWrapper = styled(ContentContainer)`
  * {
    padding-top: 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    > div {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

const StyledContentContainer = styled(ContentContainer)`
  div {
    padding-bottom: 0;
  }
`;

const CtaButtonContainer = styled.div`
  * > {
    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      align-self: start;
    }
  }
`;

export interface AwardsBlockProps {
  data: {
    reference: AwardsBlockI[];
  };
}

const AwardsBlock: React.FC<AwardsBlockProps> = ({ data }) => {
  const blockReference = data.reference[0];
  const { ctaButtons, description, header, images } = blockReference;
  const isEvenNumberOfImages = images?.length % 2 === 0;
  const indexOfLargerImage = Math.floor(images.length / 2);

  return (
    <>
      <StyledContentContainer>
        {header && <AwardsCopy richText={header} />}
        <AwardsBodyContainer>
          {description && <AwardsCopy richText={description} />}
          {ctaButtons?.map((button, buttonIndex) => {
            const { link, openInNewWindow } = getModularBlockLinkData(button.buttonLink);

            return (
              <CtaButtonContainer key={buttonIndex}>
                <CtaButton
                  to={link}
                  openInNewWindow={openInNewWindow}
                  buttonType={button.buttonType}
                  text={button.buttonText}
                  bold
                  dataLayerInfo={header}
                />
              </CtaButtonContainer>
            );
          })}
        </AwardsBodyContainer>
      </StyledContentContainer>
      <AwardImagesWrapper>
        <AwardImagesContainer>
          {images?.map((image, imageIndex) => (
            <StyledImage
              key={imageIndex}
              loading='lazy'
              $isLargeImage={!isEvenNumberOfImages && imageIndex === indexOfLargerImage}
              src={autoOptimizeImage(image.imageAsset.url)}
              alt={image.altImageText ?? 'awards image'}
            />
          ))}
        </AwardImagesContainer>
      </AwardImagesWrapper>
    </>
  );
};

export default AwardsBlock;
